import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactJson from 'react-json-view';
import { showNotificationGlobal } from '../common/NotificationProvider';

const JSON_VIEW_THEME_IAM = {
  base00: '#ffffff',
  base01: '#fafafa',
  base02: '#f0f0f0',
  base03: '#879596',
  base04: '#687078',
  base05: '#16191f',
  base06: '#16191f',
  base07: '#16191f',
  base08: '#d13212',
  base09: '#df2a5d',
  base0A: '#ca8a04',
  base0B: '#037f0c',
  base0C: '#037f0c',
  base0D: '#0073bb',
  base0E: '#7c3aed',
  base0F: '#0073bb',
};

const Ic = {
  Copy: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.5 10.5H3.5a1.5 1.5 0 0 1-1.5-1.5V3.5A1.5 1.5 0 0 1 3.5 2h5.5a1.5 1.5 0 0 1 1.5 1.5V4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Close: () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Expand: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 9.5V13h3.5M10 2.5h3.5V6M13 13h-3.5V9.5M6 6H2.5V2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

async function copyTextToClipboard(text, alertMessage = 'Copied to clipboard') {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    showNotificationGlobal(alertMessage, 'success', 'bottomRight');
  } catch {
    showNotificationGlobal('Failed to copy', 'error', 'bottomRight');
  }
}

function parseJsonData(data, jsonText) {
  if (data != null) return data;
  if (!jsonText) return {};
  try {
    return JSON.parse(jsonText);
  } catch {
    return { _raw: jsonText };
  }
}

function ColorfulJsonView({ data, jsonText, maxHeight, collapsed = false }) {
  const src = useMemo(() => parseJsonData(data, jsonText), [data, jsonText]);
  const formatted = useMemo(() => {
    if (jsonText) return jsonText;
    try {
      return JSON.stringify(src, null, 4);
    } catch {
      return '{}';
    }
  }, [jsonText, src]);
  const lineCount = useMemo(() => formatted.split('\n').length, [formatted]);

  return (
    <div className="pj-colorful-json pj-colorful-json--iam" style={{ maxHeight, background: '#ffffff' }}>
      <div className="pj-iam-json-gutter" aria-hidden="true">
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} className="pj-iam-json-ln">{i + 1}</div>
        ))}
      </div>
      <div className="pj-iam-json-code">
        <ReactJson
          src={src}
          name={false}
          collapsed={collapsed}
          groupArraysAfterLength={Number.MAX_SAFE_INTEGER}
          enableClipboard={false}
          displayDataTypes={false}
          displayObjectSize={false}
          displayArrayKey={false}
          quotesOnKeys={false}
          sortKeys={false}
          indentWidth={4}
          theme={JSON_VIEW_THEME_IAM}
          style={{
            background: 'transparent',
            fontSize: 13,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        />
      </div>
    </div>
  );
}

export function PolicyIamJsonPanel({
  data,
  jsonText,
  title = 'Policy JSON',
  subtitle = 'Policy document',
  maxHeight = 320,
  className = '',
  showFullView = true,
}) {
  const [showModal, setShowModal] = useState(false);
  const src = useMemo(() => parseJsonData(data, jsonText), [data, jsonText]);
  const formatted = useMemo(() => jsonText || JSON.stringify(src, null, 4), [jsonText, src]);
  const statementSid = src?.statements?.[0]?.sid;
  const displayTitle = title || statementSid || 'Policy JSON';
  const displaySubtitle = subtitle || (statementSid ? `Statement: ${statementSid}` : 'Formatted policy document');

  return (
    <>
      <PolicyJsonViewerStyles />
      <div className={`pj-iam-json-panel ${className}`.trim()}>
        <div className="pj-iam-json-header">
          <div className="pj-iam-json-header-text">
            <div className="pj-iam-json-title">{displayTitle}</div>
            <div className="pj-iam-json-subtitle">{displaySubtitle}</div>
          </div>
          <div className="pj-iam-json-header-actions">
            {showFullView ? (
              <button
                type="button"
                className="pj-iam-json-full-btn"
                onClick={() => setShowModal(true)}
                title="Open full JSON view"
              >
                <Ic.Expand />
                Full view
              </button>
            ) : null}
            <button
              type="button"
              className="pj-iam-json-copy-btn"
              onClick={() => copyTextToClipboard(formatted, 'JSON copied to clipboard')}
            >
              <Ic.Copy />
              Copy Policy
            </button>
          </div>
        </div>
        <ColorfulJsonView data={data} jsonText={formatted} maxHeight={maxHeight} />
      </div>
      {showModal ? (
        <PolicyJsonModal
          data={src}
          jsonText={formatted}
          title={displayTitle}
          subtitle={displaySubtitle}
          onClose={() => setShowModal(false)}
        />
      ) : null}
    </>
  );
}

export function PolicyJsonModal({
  data,
  jsonText,
  title = 'Policy JSON',
  subtitle = 'Full policy document',
  onClose,
  loading = false,
  error = null,
}) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const formatted = jsonText || (data != null ? JSON.stringify(data, null, 4) : '');
  const canCopy = !loading && !error && Boolean(formatted);

  return createPortal(
    <div className="pj-json-modal-overlay" onClick={onClose} role="presentation">
      <PolicyJsonViewerStyles />
      <div
        className="pj-json-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pj-iam-json-header pj-iam-json-header--modal">
          <div className="pj-iam-json-header-text">
            <div className="pj-iam-json-title">{title}</div>
            <div className="pj-iam-json-subtitle">{subtitle}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="pj-iam-json-copy-btn"
              disabled={!canCopy}
              onClick={() => copyTextToClipboard(formatted, 'JSON copied to clipboard')}
              style={canCopy ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
            >
              <Ic.Copy />
              Copy Policy
            </button>
            <button type="button" title="Close" className="pj-iam-json-close-btn" onClick={onClose}>
              <Ic.Close />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div className="pj-json-modal-state">
              <div className="pj-json-modal-spinner" aria-hidden="true" />
              <div className="pj-json-modal-state-title">Loading policy…</div>
              <div className="pj-json-modal-state-sub">Fetching document from storage</div>
            </div>
          ) : error ? (
            <div className="pj-json-modal-state">
              <div className="pj-json-modal-state-title" style={{ color: '#dc2626' }}>Unable to load policy</div>
              <div className="pj-json-modal-state-sub">{error}</div>
            </div>
          ) : (
            <ColorfulJsonView data={data} jsonText={formatted} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function PolicyJsonModalTrigger({
  policyDocument,
  onClickStop,
  title = 'Policy JSON',
  subtitle = 'Formatted policy document',
  buttonLabel = 'View Policy JSON',
  className = '',
  accentColor = '#3b82f6',
  compact = false,
}) {
  const [showModal, setShowModal] = useState(false);

  if (!policyDocument) return null;

  const jsonText = JSON.stringify(policyDocument, null, 4);
  const statementCount = Array.isArray(policyDocument?.statements) ? policyDocument.statements.length : 0;
  const displaySubtitle = subtitle || (statementCount
    ? `${statementCount} statement${statementCount === 1 ? '' : 's'}`
    : 'Formatted policy document');

  return (
    <>
      <PolicyJsonViewerStyles />
      {showModal && (
        <PolicyJsonModal
          data={policyDocument}
          jsonText={jsonText}
          title={title}
          subtitle={displaySubtitle}
          onClose={() => setShowModal(false)}
        />
      )}
      <button
        type="button"
        className={`pj-policy-json-view-btn${compact ? ' pj-policy-json-view-btn--compact' : ''} ${className}`.trim()}
        onClick={(e) => {
          onClickStop?.(e);
          e.stopPropagation();
          setShowModal(true);
        }}
      >
        <span style={{ display: 'inline-flex', color: compact ? undefined : accentColor }}><Ic.Expand /></span>
        {buttonLabel}
      </button>
    </>
  );
}

export function PolicyJsonViewerStyles() {
  return (
    <style>{`
      .pj-policy-json-view-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 34px;
        padding: 0 14px;
        border-radius: 8px;
        border: 1px solid rgba(0,0,0,0.1);
        background: #ffffff;
        color: #0f172a;
        font-family: 'DM Sans','Segoe UI',sans-serif;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease;
      }
      .pj-policy-json-view-btn:hover {
        background: #f8fafc;
        border-color: rgba(59, 130, 246, 0.35);
      }
      .pj-policy-json-view-btn--block {
        width: 100%;
        justify-content: center;
      }
      .pj-policy-json-view-btn--compact {
        height: 28px;
        padding: 0 10px;
        gap: 5px;
        font-size: 11px;
        font-weight: 600;
        border-radius: 6px;
        border-color: rgba(59, 130, 246, 0.25);
        color: #2563eb;
        background: #eff6ff;
        flex-shrink: 0;
      }
      .pj-policy-json-view-btn--compact:hover {
        background: #dbeafe;
        border-color: rgba(59, 130, 246, 0.4);
      }
      .pj-policy-json-view-btn--compact svg {
        width: 12px;
        height: 12px;
      }
      .pj-iam-json-panel {
        border: 1px solid #d5dbdb;
        border-radius: 8px;
        overflow: hidden;
        background: #ffffff;
      }
      .pj-json-modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
      }
      .pj-json-modal {
        width: min(920px, 100%);
        max-height: min(88vh, 900px);
        display: flex;
        flex-direction: column;
        background: #ffffff;
        border: 1px solid #d5dbdb;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
      .pj-iam-json-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 16px 20px;
        border-bottom: 1px solid #eaeded;
        background: #ffffff;
      }
      .pj-iam-json-header--modal { flex-shrink: 0; }
      .pj-iam-json-header-text { min-width: 0; }
      .pj-iam-json-header-actions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }
      .pj-iam-json-title {
        font-size: 18px;
        font-weight: 700;
        color: #16191f;
        line-height: 1.3;
      }
      .pj-iam-json-subtitle {
        font-size: 13px;
        color: #545b64;
        margin-top: 4px;
        line-height: 1.4;
      }
      .pj-iam-json-full-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;
        border: 1px solid #d5dbdb;
        background: #ffffff;
        color: #16191f;
        font-family: 'DM Sans','Segoe UI',sans-serif;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        flex-shrink: 0;
        white-space: nowrap;
      }
      .pj-iam-json-full-btn:hover { background: #f2f3f3; border-color: #0073bb; color: #0073bb; }
      .pj-iam-json-full-btn svg { display: block; }
      .pj-iam-json-copy-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;
        border: 1px solid #0073bb;
        background: #ffffff;
        color: #0073bb;
        font-family: 'DM Sans','Segoe UI',sans-serif;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        flex-shrink: 0;
        white-space: nowrap;
      }
      .pj-iam-json-copy-btn:hover { background: #f1faff; }
      .pj-iam-json-copy-btn svg { display: block; }
      .pj-iam-json-close-btn {
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 8px;
        border: 1px solid #d5dbdb;
        background: #ffffff;
        color: #545b64;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .pj-iam-json-close-btn:hover { background: #f2f3f3; }
      .pj-colorful-json {
        flex: 1;
        min-height: 0;
        overflow: auto;
        display: flex;
        align-items: flex-start;
      }
      .pj-colorful-json--iam {
        background: #ffffff;
        border-top: 1px solid #eaeded;
      }
      .pj-iam-json-gutter {
        flex-shrink: 0;
        padding: 12px 0;
        background: #fafafa;
        border-right: 1px solid #eaeded;
        user-select: none;
        min-width: 44px;
        text-align: right;
      }
      .pj-iam-json-ln {
        padding: 0 12px 0 8px;
        font-size: 13px;
        line-height: 1.65;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        color: #879596;
      }
      .pj-iam-json-code {
        flex: 1;
        min-width: 0;
        padding: 12px 16px 12px 8px;
        overflow: auto;
      }
      .pj-colorful-json--iam .object-key-val,
      .pj-colorful-json--iam .variable-row {
        border-left: 1px solid #eaeded !important;
        padding-left: 8px !important;
        margin-left: 4px !important;
      }
      .pj-colorful-json--iam .object-key { color: #0073bb !important; }
      .pj-colorful-json--iam .string-value { color: #037f0c !important; }
      .pj-colorful-json--iam .brace-row > span,
      .pj-colorful-json--iam .array-key { color: #16191f !important; }
      .pj-json-modal-state {
        flex: 1;
        min-height: 280px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 40px 24px;
        background: #ffffff;
      }
      .pj-json-modal-state-title {
        font-family: 'DM Sans','Segoe UI',sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: #0f172a;
      }
      .pj-json-modal-state-sub {
        font-family: 'DM Sans','Segoe UI',sans-serif;
        font-size: 12px;
        color: #64748b;
        text-align: center;
        max-width: 360px;
        line-height: 1.45;
      }
      .pj-json-modal-spinner {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 2.5px solid #e2e8f0;
        border-top-color: #3b82f6;
        animation: pj-spin 0.7s linear infinite;
        margin-bottom: 6px;
      }
      @keyframes pj-spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  );
}
